import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CircleStackIcon, 
  ServerStackIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
  ClockIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import axiosInstance from '../utils/axios';

const DatabaseHealthManager = ({ deviceId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [databaseHealth, setDatabaseHealth] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const eventSourceRef = useRef(null);

  // Fetch database health report via API
  const fetchDatabaseHealth = useCallback(async () => {
    if (!deviceId) return;
    
    setIsLoading(true);
    try {
      const response = await axiosInstance.post(`/v1/database/device/${deviceId}/request-health`);
      
      if (response.data.success) {
        console.log('Database health report requested, waiting for SSE response...');
      }
    } catch (error) {
      console.error('Failed to fetch database health:', error);
    } finally {
      // Don't set loading to false immediately - wait for SSE response
      setTimeout(() => setIsLoading(false), 3000);
    }
  }, [deviceId]);

  // Setup SSE for real-time database health updates
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    const sseUrl = import.meta.env.VITE_SSE_BASE_URL || 'http://localhost:3012/api/sse';
    const eventSource = new EventSource(`${sseUrl}/events?token=${token}`);
    eventSourceRef.current = eventSource;
    
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        // Handle database health report
        if (data.type === 'database_health_report' && data.deviceId === deviceId) {
          console.log('Received database health report via SSE:', data);
          setDatabaseHealth(data.report);
          setLastUpdate(new Date());
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error parsing SSE message:', error);
      }
    };
    
    eventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
      eventSource.close();
    };
    
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [deviceId]);

  // Auto-fetch on mount
  useEffect(() => {
    if (!databaseHealth) {
      fetchDatabaseHealth();
    }
  }, [databaseHealth, fetchDatabaseHealth]);

  // Format numbers
  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const hours = Math.floor(mins / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${mins % 60}m`;
    return `${mins}m`;
  };

  // Get health status color
  const getHealthColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-green-600 bg-green-50';
      case 'warning': return 'text-yellow-600 bg-yellow-50';
      case 'critical': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  // Get connection pool health
  const getPoolHealth = (pool) => {
    if (!pool) return { color: 'gray', text: 'Unknown' };
    const usage = parseFloat(pool.usagePercent || 0);
    
    if (usage < 50) return { color: 'green', text: 'Healthy' };
    if (usage < 80) return { color: 'yellow', text: 'Warning' };
    return { color: 'red', text: 'Critical' };
  };

  // Quick status when collapsed
  const getQuickStatus = () => {
    if (!databaseHealth) return null;
    
    const connections = databaseHealth.connections?.stats;
    const health = databaseHealth.health;
    const overview = databaseHealth.overview;
    
    return {
      connected: connections?.total || 0,
      active: connections?.active || 0,
      max: connections?.max_connections || 100,
      usage: connections?.connection_usage_percent?.toFixed(1) || 0,
      healthScore: health?.score || 0,
      healthStatus: health?.status || 'unknown',
      size: overview?.size_human || 'N/A'
    };
  };

  const quickStatus = getQuickStatus();

  return (
    <div className="space-y-4">
      {/* Quick Status Bar */}
      {quickStatus && (
        <div className="bg-gray-50 rounded-lg px-4 py-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">Live database health on this Dryer Master</p>
            <div className="flex items-center gap-4 text-sm">
              <div className={`px-2 py-1 rounded-full font-medium ${getHealthColor(quickStatus.healthStatus)}`}>
                Score: {quickStatus.healthScore}%
              </div>
              <div className="text-gray-600">
                <span className="font-medium">{quickStatus.connected}/{quickStatus.max}</span> connections
                <span className="ml-2 text-gray-500">({quickStatus.usage}% used)</span>
              </div>
              <div className="text-gray-600">
                Size: <span className="font-medium">{quickStatus.size}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6">
              {/* Action Bar */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  {lastUpdate && (
                    <>
                      <ClockIcon className="w-4 h-4" />
                      Last updated: {lastUpdate.toLocaleTimeString()}
                    </>
                  )}
                </div>
                <button
                  onClick={fetchDatabaseHealth}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  <ArrowPathIcon className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>

              {isLoading && !databaseHealth ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <ArrowPathIcon className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-2" />
                    <p className="text-gray-500">Loading database metrics...</p>
                  </div>
                </div>
              ) : databaseHealth ? (
                <div className="space-y-6">
                  {/* Health Overview */}
                  {databaseHealth.health && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Health Overview</h4>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Health Score</p>
                          <div className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${getHealthColor(databaseHealth.health.status)}`}>
                            {databaseHealth.health.score}% - {databaseHealth.health.status}
                          </div>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Database Size</p>
                          <p className="text-lg font-semibold">{databaseHealth.overview?.size_human || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Total Tables</p>
                          <p className="text-lg font-semibold">{databaseHealth.overview?.table_count || 0}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Cache Hit Ratio</p>
                          <p className="text-lg font-semibold">{databaseHealth.cache?.cache_hit_ratio || 0}%</p>
                        </div>
                      </div>
                      
                      {/* Issues and Warnings */}
                      {(databaseHealth.health.issues?.length > 0 || databaseHealth.health.warnings?.length > 0) && (
                        <div className="mt-4 space-y-2">
                          {databaseHealth.health.issues?.map((issue, idx) => (
                            <div key={`issue-${idx}`} className="flex items-start gap-2 p-2 bg-red-50 rounded">
                              <ExclamationTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0" />
                              <p className="text-sm text-red-800">{issue}</p>
                            </div>
                          ))}
                          {databaseHealth.health.warnings?.map((warning, idx) => (
                            <div key={`warning-${idx}`} className="flex items-start gap-2 p-2 bg-yellow-50 rounded">
                              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                              <p className="text-sm text-yellow-800">{warning}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Connection Pools */}
                  {databaseHealth.connections?.appPools && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Application Connection Pools</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {Object.entries(databaseHealth.connections.appPools).map(([key, pool]) => {
                          const health = getPoolHealth(pool);
                          return (
                            <div key={key} className="bg-gray-50 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-3">
                                <h5 className="font-medium text-gray-900">{pool.name}</h5>
                                <span className={`w-2 h-2 rounded-full bg-${health.color}-500`} />
                              </div>
                              
                              <div className="space-y-2">
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">Connections</span>
                                  <span className="font-medium">{pool.used}/{pool.max}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">Active</span>
                                  <span className="font-medium text-green-600">{pool.active}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">Available</span>
                                  <span className="font-medium">{pool.available}</span>
                                </div>
                                
                                <div className="pt-2">
                                  <div className="flex justify-between text-xs mb-1">
                                    <span className="text-gray-600">Usage</span>
                                    <span className={`font-medium text-${health.color}-600`}>
                                      {pool.usagePercent}%
                                    </span>
                                  </div>
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div 
                                      className={`h-2 rounded-full bg-${health.color}-500 transition-all`}
                                      style={{ width: `${Math.min(pool.usagePercent, 100)}%` }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Active Connections Table */}
                  {databaseHealth.connections?.activeConnections?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">
                        Active Connections ({databaseHealth.connections.activeConnections.length})
                      </h4>
                      <div className="bg-gray-50 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="min-w-full">
                            <thead className="bg-gray-100 border-b border-gray-200">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">PID</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Application</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">User</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Client</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">State</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Duration</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Query</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {databaseHealth.connections.activeConnections.slice(0, 15).map((conn, idx) => {
                                const appIcon = conn.appType === 'embedded' ? '🔧' : 
                                               conn.appType === 'iot' ? '📡' :
                                               conn.appType === 'terminal' ? '💻' :
                                               conn.appType === 'tool' ? '🛠️' : '❓';
                                const stateColor = conn.state === 'active' ? 'text-green-600' :
                                                 conn.state === 'idle' ? 'text-gray-500' :
                                                 conn.state === 'idle in transaction' ? 'text-yellow-600' : 
                                                 'text-gray-600';
                                
                                return (
                                  <tr key={`conn-${idx}`} className="hover:bg-gray-50">
                                    <td className="px-3 py-2 text-xs text-gray-900">{conn.pid}</td>
                                    <td className="px-3 py-2 text-xs">
                                      <span className="mr-1">{appIcon}</span>
                                      {conn.application}
                                    </td>
                                    <td className="px-3 py-2 text-xs text-gray-600">{conn.username}</td>
                                    <td className="px-3 py-2 text-xs text-gray-600">
                                      {conn.clientAddress}
                                      {conn.clientPort && `:${conn.clientPort}`}
                                    </td>
                                    <td className={`px-3 py-2 text-xs font-medium ${stateColor}`}>
                                      {conn.state}
                                    </td>
                                    <td className="px-3 py-2 text-xs text-gray-600">
                                      {formatDuration(conn.connectionDuration)}
                                    </td>
                                    <td className="px-3 py-2 text-xs text-gray-500 max-w-xs">
                                      <div className="truncate" title={conn.currentQuery}>
                                        {conn.currentQuery === 'idle' ? 
                                          <span className="text-gray-400">idle</span> : 
                                          conn.currentQuery?.substring(0, 50) + 
                                          (conn.currentQuery?.length > 50 ? '...' : '')
                                        }
                                      </div>
                                    </td>
                                    <td className="px-3 py-2 text-xs">
                                      {conn.state === 'active' && (
                                        <button
                                          onClick={() => handleTerminateConnection(conn.pid)}
                                          className="text-red-600 hover:text-red-700"
                                          title="Terminate connection"
                                        >
                                          <XMarkIcon className="w-4 h-4" />
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          {databaseHealth.connections.activeConnections.length > 15 && (
                            <div className="px-3 py-2 bg-gray-50 text-center text-xs text-gray-500">
                              Showing 15 of {databaseHealth.connections.activeConnections.length} connections
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Tables & Partitions */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Table Stats */}
                    {databaseHealth.tables && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-3">Table Statistics</h4>
                        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Total Tables</span>
                            <span className="text-sm font-medium">{databaseHealth.tables.summary?.total_tables || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Total Rows</span>
                            <span className="text-sm font-medium">{formatNumber(databaseHealth.tables.summary?.total_rows || 0)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Dead Rows</span>
                            <span className="text-sm font-medium">{formatNumber(databaseHealth.tables.summary?.total_dead_rows || 0)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Total Size</span>
                            <span className="text-sm font-medium">
                              {formatNumber(Math.round((databaseHealth.tables.summary?.total_size || 0) / 1024 / 1024))} MB
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Partition Stats */}
                    {databaseHealth.partitions && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-3">Partition Statistics</h4>
                        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Total Partitions</span>
                            <span className="text-sm font-medium">{databaseHealth.partitions.summary?.total_partitions || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Hot (&lt; 30 days)</span>
                            <span className="text-sm font-medium text-green-600">{databaseHealth.partitions.summary?.hot_partitions || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Warm (30-90 days)</span>
                            <span className="text-sm font-medium text-yellow-600">{databaseHealth.partitions.summary?.warm_partitions || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Old (&gt; 90 days)</span>
                            <span className="text-sm font-medium text-gray-600">{databaseHealth.partitions.summary?.old_partitions || 0}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Maintenance Actions */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Maintenance Actions</h4>
                    <div className="flex gap-3">
                      <button
                        onClick={handleRunVacuum}
                        className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                      >
                        Run VACUUM ANALYZE
                      </button>
                      <button
                        onClick={handleRefreshMetrics}
                        className="px-4 py-2 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700"
                      >
                        Force Refresh Metrics
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <CircleStackIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p>No database metrics available</p>
                  <button
                    onClick={fetchDatabaseHealth}
                    className="mt-3 text-blue-600 hover:text-blue-700"
                  >
                    Load Database Health
                  </button>
                </div>
              )}
            </div>
      </div>
    </div>
  );

  // Handler functions
  async function handleTerminateConnection(pid) {
    if (!confirm(`Terminate connection ${pid}?`)) return;
    
    try {
      const response = await axiosInstance.post(`/v1/database/device/${deviceId}/terminate`, {
        pid
      });
      
      if (response.data.success) {
        console.log(`Connection ${pid} terminated`);
        setTimeout(fetchDatabaseHealth, 1000);
      }
    } catch (error) {
      console.error('Failed to terminate connection:', error);
    }
  }

  async function handleRunVacuum() {
    if (!confirm('Run VACUUM ANALYZE on database? This may take some time.')) return;
    
    try {
      const response = await axiosInstance.post(`/v1/database/device/${deviceId}/vacuum`, {
        analyze: true
      });
      
      if (response.data.success) {
        console.log('VACUUM ANALYZE started...');
      }
    } catch (error) {
      console.error('Failed to run VACUUM:', error);
    }
  }

  async function handleRefreshMetrics() {
    try {
      const response = await axiosInstance.post(`/v1/database/device/${deviceId}/refresh`);
      
      if (response.data.success) {
        console.log('Refreshing database metrics...');
        setIsLoading(true);
        setTimeout(() => setIsLoading(false), 3000);
      }
    } catch (error) {
      console.error('Failed to refresh metrics:', error);
    }
  }
};

export default DatabaseHealthManager;